import React, { useEffect, useState } from "react";
import { Box, Button, Divider, IconButton, Modal, TextField, Typography, } from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import { useUpdateUsuarioPermiso } from "../../../hooks/AdministracionUsuarios/adminPermisosUsuarios/updateUsuarioPermiso";
import { sanitizarNombrePermiso, sanitizarNombrePerfil, sanitizarDescripcionPerfil, validarNombrePerfil, validarDescripcionPerfil, } from "../../../utils/sanitizar";

interface PermisoEditar {
    id: number;
    nombre: string;
    descripcion: string;
    sufijo: string;
    estatus: number;
}

interface UpdateAdminPermisoProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => Promise<void> | void;
    permiso: PermisoEditar | null;
}

const UpdateAdminPermiso: React.FC<UpdateAdminPermisoProps> = ({
    open,
    onClose,
    onSuccess,
    permiso,
}) => {

    const {
        actualizarPermiso,
        loading,
    } = useUpdateUsuarioPermiso();

    const [nombre, setNombre] = useState("");
    const [descripcion, setDescripcion] = useState("");
    const [sufijo, setSufijo] = useState("");

    const [errorNombre, setErrorNombre] = useState("");
    const [errorDescripcion, setErrorDescripcion] = useState("");
    const [errorSufijo, setErrorSufijo] = useState("");

    useEffect(() => {

        if (open && permiso) {

            setNombre(permiso.nombre);
            setDescripcion(permiso.descripcion);
            setSufijo(permiso.sufijo);

            setErrorNombre("");
            setErrorDescripcion("");
            setErrorSufijo("");

        }

    }, [open, permiso]);

    const limpiarFormulario = () => {

        setNombre("");
        setDescripcion("");
        setSufijo("");

        setErrorNombre("");
        setErrorDescripcion("");
        setErrorSufijo("");

    };

    const cerrarModal = () => {

        limpiarFormulario();

        onClose();

    };

    const guardar = async () => {

        setErrorNombre("");
        setErrorDescripcion("");
        setErrorSufijo("");

        let existeError = false;

        if (!validarNombrePerfil(nombre)) {

            setErrorNombre("Ingresa un nombre válido.");

            existeError = true;

        }

        if (!validarDescripcionPerfil(descripcion)) {

            setErrorDescripcion(
                "Ingresa una descripción válida."
            );

            existeError = true;

        }

        if (!validarNombrePerfil(sufijo)) {

            setErrorSufijo(
                "Ingresa un sufijo válido."
            );

            existeError = true;

        }

        if (existeError) {
            return;
        }

        const response = await actualizarPermiso({

            id: permiso!.id,
            nombre: nombre.trim(),
            descripcion: descripcion.trim(),
            sufijo: sufijo.trim(),

        });

        if (response) {

            await onSuccess?.();

            cerrarModal();

        }

    };

    return (

        <Modal
            open={open}
            onClose={cerrarModal}
        >

            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "90%",
                    maxWidth: 650,
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 3,
                }}
            >

                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                >

                    <Typography
                        variant="h6"
                        fontWeight="bold"
                    >
                        Editar Permiso
                    </Typography>

                    <IconButton onClick={cerrarModal}>
                        <CloseIcon />
                    </IconButton>

                </Box>

                <Divider sx={{ mb: 3 }} />

                <Box
                    display="flex"
                    flexDirection="column"
                    gap={3}
                >

                    <TextField
                        fullWidth
                        label="Nombre"
                        value={nombre}
                        error={Boolean(errorNombre)}
                        helperText={errorNombre}
                        onChange={(e) => {

                            const value =
                                sanitizarNombrePermiso(
                                    e.target.value
                                );

                            setNombre(value);

                            if (
                                errorNombre &&
                                validarNombrePerfil(value)
                            ) {
                                setErrorNombre("");
                            }

                        }}
                    />

                    <TextField
                        fullWidth
                        label="Sufijo"
                        value={sufijo}
                        error={Boolean(errorSufijo)}
                        helperText={errorSufijo}
                        onChange={(e) => {

                            const value =
                                sanitizarNombrePerfil(
                                    e.target.value
                                );

                            setSufijo(value);

                            if (errorSufijo) {
                                setErrorSufijo("");
                            }

                        }}
                    />

                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Descripción"
                        value={descripcion}
                        error={Boolean(errorDescripcion)}
                        helperText={errorDescripcion}
                        onChange={(e) => {

                            const value =
                                sanitizarDescripcionPerfil(
                                    e.target.value
                                );

                            setDescripcion(value);

                            if (errorDescripcion) {
                                setErrorDescripcion("");
                            }

                        }}
                    />

                </Box>

                <Divider sx={{ my: 3 }} />

                <Box
                    display="flex"
                    justifyContent="flex-end"
                    gap={2}
                >

                    <Button
                        variant="contained"
                        onClick={cerrarModal}
                        sx={{
                            bgcolor: "#F70083",
                            color: "#fff",
                            fontWeight: 600,
                            textTransform: "none",
                            borderRadius: 2,
                            px: 3,
                            "&:hover": {
                                bgcolor: "#D60072",
                            },
                        }}
                    >
                        Cancelar
                    </Button>

                    <Button
                        variant="contained"
                        onClick={guardar}
                        disabled={
                            loading ||
                            !validarNombrePerfil(nombre) ||
                            !validarNombrePerfil(sufijo) ||
                            !validarDescripcionPerfil(descripcion)
                        }
                        sx={{
                            bgcolor: "#2e7d32",
                            color: "#fff",
                            fontWeight: 600,
                            textTransform: "none",
                            borderRadius: 2,
                            px: 3,
                            "&:hover": {
                                bgcolor: "#1b5e20",
                            },
                        }}
                    >
                        {loading
                            ? "Guardando..."
                            : "Guardar Cambios"}
                    </Button>

                </Box>

            </Box>

        </Modal>

    );

};

export default UpdateAdminPermiso;